import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/news';

export default function NewsNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/arama?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  // Gizli admin erişimi: logo'ya 5 kez tıklayınca
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [logoClickTimer, setLogoClickTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleLogoClick = () => {
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    if (logoClickTimer) clearTimeout(logoClickTimer);
    if (newCount >= 5) {
      setLogoClickCount(0);
      window.location.href = '/admin';
      return;
    }
    const timer = setTimeout(() => setLogoClickCount(0), 2000);
    setLogoClickTimer(timer);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0A0E27]/95 backdrop-blur-md shadow-lg' : 'bg-[#0A0E27]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
            <img
              src="https://public.readdy.ai/ai/img_res/27f9ab70-330d-4ebc-8660-fbee76b65610.png"
              alt="Teknonova"
              className="h-8 w-auto object-contain"
            />
            <span className="text-white font-bold text-xl tracking-tight">Teknonova</span>
          </div>

          {/* Desktop Search */}
          <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
            <form onSubmit={handleSearch} className="w-full relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Haber ara..."
                className="w-full bg-white/10 border border-white/10 text-white placeholder-white/40 text-sm px-4 py-2 pl-10 rounded-full outline-none focus:border-[#00D9FF]/50 transition-colors"
              />
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            </form>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/okuma-listesi"
              className="w-9 h-9 flex items-center justify-center bg-white/5 border border-white/10 rounded-full text-white/60 hover:text-[#00D9FF] hover:border-[#00D9FF]/30 transition-all cursor-pointer"
              title="Okuma Listesi"
            >
              <i className="ri-bookmark-line text-base" />
            </Link>
            <Link
              to="/populer"
              className="w-9 h-9 flex items-center justify-center bg-white/5 border border-white/10 rounded-full text-white/60 hover:text-[#FF6B6B] hover:border-[#FF6B6B]/30 transition-all cursor-pointer"
              title="Popüler Haberler"
            >
              <i className="ri-fire-line text-base" />
            </Link>
            <Link
              to="/abonelik"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D9FF] text-[#0A0E27] text-sm font-semibold rounded-full hover:bg-[#00D9FF]/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line" />
              Abone Ol
            </Link>
          </div>

          {/* Mobile Buttons */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              className="text-white text-xl p-2 cursor-pointer"
              onClick={() => setSearchOpen(!searchOpen)}
            >
              <i className={searchOpen ? 'ri-close-line' : 'ri-search-line'} />
            </button>
            <button
              className="text-white text-xl p-2 cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        {searchOpen && (
          <div className="md:hidden px-4 pb-4">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Haber ara..."
                autoFocus
                className="w-full bg-white/10 border border-white/10 text-white placeholder-white/40 text-sm px-4 py-2 pl-10 rounded-full outline-none focus:border-[#00D9FF]/50 transition-colors"
              />
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            </form>
          </div>
        )}

        {/* Categories Bar */}
        <div className="hidden md:flex items-center gap-1 pb-3 overflow-x-auto">
          <Link
            to="/"
            className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
              location.pathname === '/' && !location.search
                ? 'bg-white text-[#0A0E27]' 
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            Ana Sayfa
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/kategori/${cat.id}`}
              className={`px-4 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                location.pathname.includes(cat.id)
                  ? 'bg-white text-[#0A0E27]' 
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A0E27]/98 backdrop-blur-md border-t border-white/10 px-6 py-4">
          <ul className="flex flex-col gap-2">
            <li>
              <Link to="/" className="block text-white font-medium py-2 cursor-pointer">Ana Sayfa</Link>
            </li>
            {categories.map((cat) => (
              <li key={cat.id}>
                <Link to={`/kategori/${cat.id}`} className="block text-white/80 py-2 cursor-pointer">
                  {cat.name}
                </Link>
              </li>
            ))}
            <li className="pt-2 border-t border-white/10 mt-2">
              <Link
                to="/abonelik"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D9FF] text-[#0A0E27] text-sm font-semibold rounded-full cursor-pointer"
              >
                <i className="ri-mail-line" />
                Bültene Abone Ol
              </Link>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}
